import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { IoGlobeOutline } from "react-icons/io5";
import { FiSend } from "react-icons/fi";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import { toast } from "react-toastify";
import LoadingComponent from "../../Components/LoadingComponent";

export default function RegisterDomain() {
  const { token } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();
  const [loading, setLoading] = useState(false);

  const handleRegisterDomain = async (data) => {
    try {
      setLoading(true);
      const { data: resData } = await axios.post(
        `${server}/domain/new`,
        {
          domainName: data?.domainName,
          ipAddress: data?.ipAddress,
          expirationDate: data?.expirationDate
            ? Date.parse(data?.expirationDate)
            : "",
          alertDate: data?.alertDate ? Date.parse(data?.alertDate) : "",
          remarks: data?.remarks || "",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(resData?.message || "Domain registered successfully!");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center p-4">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="bg-white p-8 rounded-3xl shadow-xl w-full max-w-lg border"
      >
        {/* Title */}
        <div className="flex items-center gap-3 mb-6">
          <IoGlobeOutline className="text-3xl text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            Register New Domain
          </h2>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleRegisterDomain)}
          className="space-y-5"
        >
          {/* Domain Name */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Domain Name
            </label>
            <input
              type="text"
              {...register("domainName", {
                required: "Domain name is required",
              })}
              placeholder="e.g., example.com"
              className={`h-11 px-4 w-full border ${
                errors.domainName ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.domainName && (
              <p className="text-sm text-red-500 mt-1">
                {errors.domainName.message}
              </p>
            )}
          </div>

          {/* IP Address */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              IP Address
            </label>
            <input
              type="text"
              {...register("ipAddress", {
                required: false,
              })}
              placeholder="e.g., xxx.xxx.xx.xx"
              className={`h-11 px-4 w-full border ${
                errors.ipAddress ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.ipAddress && (
              <p className="text-sm text-red-500 mt-1">
                {errors.ipAddress.message}
              </p>
            )}
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Expiration Date
            </label>
            <input
              type="date"
              {...register("expirationDate", {
                required: "Expiration date is required",
              })}
              className={`h-11 px-4 w-full border ${
                errors.expirationDate ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.expirationDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.expirationDate.message}
              </p>
            )}
          </div>

          {/* Reminder Date */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Reminder Date
            </label>
            <input
              type="date"
              {...register("alertDate", {
                required: "Alert date is required",
              })}
              className={`h-11 px-4 w-full border ${
                errors.alertDate ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50`}
            />
            {errors.alertDate && (
              <p className="text-sm text-red-500 mt-1">
                {errors.alertDate.message}
              </p>
            )}
          </div>

          {/* Remarks */}
          <div>
            <label className="block mb-1 font-medium text-gray-700">
              Remarks
            </label>
            <textarea
              {...register("remarks", {
                required: false,
                maxLength: {
                  value: 250,
                  message: "Remarks must be under 250 characters",
                },
              })}
              placeholder="Any additional notes..."
              rows="3"
              className={`px-4 py-2 w-full border ${
                errors.remarks ? "border-red-400" : "border-gray-300"
              } rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-gray-50 resize-none`}
            />
            {errors.remarks && (
              <p className="text-sm text-red-500 mt-1">
                {errors.remarks.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            type="submit"
            className="w-full flex items-center justify-center gap-2 h-11 bg-blue-600 text-white rounded-lg font-semibold tracking-wide hover:bg-blue-700 transition duration-300"
          >
            <FiSend className="text-lg" />
            Register Domain
          </motion.button>
        </form>
      </motion.div>

      {loading && <LoadingComponent />}
    </div>
  );
}
